import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { FinanceProgramForm } from '../../../../schema';
import { FinanceCriteriaOptions } from '../../../../types';
import { ChooseCriteriaRuleField } from './ChooseCriteriaRuleField';
import { CriteriaAutocompleteField } from './CriteriaAutocompleteField';
import { allFacts, getInitialCriteriaConditionFormValue } from '../../../../constants';
import { FormSection } from '../../FormSection';

interface Props {
  isReadOnly: boolean;
  userType?: 'lender' | 'dealer';
  criteriaOptions: FinanceCriteriaOptions[];
  dealerVendorProfileOptions?: any[];
}

export const ChooseCriteria = ({ criteriaOptions, isReadOnly, userType, dealerVendorProfileOptions = [] }: Props) => {
  const { control, getValues, setValue, watch, resetField } = useFormContext<FinanceProgramForm>();

  const handleReset = useCallback(() => {
    resetField('program.first.criteria');
  }, [resetField]);
  const { fields, append, remove } = useFieldArray<FinanceProgramForm>({
    control,
    name: 'program.first.criteria.conditions',
  });

  const dealerOptions = (dealerVendorProfileOptions ?? []).map(profile => {
    return {
      value: profile.id,
      label: profile.name.replace('VP - ', ''),
      ...profile,
    };
  });

  // Filter out DEALERS fact for non-lender users
  const availableFacts = useMemo(() => {
    if (userType === 'lender') {
      return allFacts;
    }
    return allFacts.filter(fact => fact.value !== FACT_NAMES_STRING.DEALERS);
  }, [userType]);

  const handleAppendCriteriaRule = () => {
    const criteriaValue = getValues('program.first.criteria.criteria');
    const selectedRules = getValues('program.first.criteria.conditions').map(({ fact }) => fact);

    if (criteriaValue) setValue('program.first.criteria.criteria', null);

    const nextRule = availableFacts.filter(fact => !selectedRules.includes(fact.value))[0].value;

    const nextCriteriaInitialValue = getInitialCriteriaConditionFormValue(nextRule);
    append(nextCriteriaInitialValue);
  };

  const handleRemoveCriteriaRule = (fieldIndex: number) => {
    const criteriaValue = getValues('program.first.criteria.criteria');
    if (criteriaValue) setValue('program.first.criteria.criteria', null);

    remove(fieldIndex);
  };

  useEffect(() => {
    const subscription = watch((value, { type }) => {
      if (value?.program?.first?.criteria?.criteria && type === 'change') {
        setValue('program.first.criteria.criteria', null);
      }
    });

    return subscription.unsubscribe;
  }, []);

  return (
    <FormSection title="Choose Criteria" onReset={handleReset} isReadOnly={isReadOnly}>
      <CriteriaAutocompleteField
        isReadOnly={isReadOnly}
        criteriaOptions={criteriaOptions}
        dealerVendorProfileOptions={dealerVendorProfileOptions}
      />

      {!!fields?.length &&
        fields.map((field, index) => (
          <ChooseCriteriaRuleField
            key={field.id}
            index={index}
            userType={userType}
            dealerOptions={dealerOptions}
            onRemove={() => handleRemoveCriteriaRule(index)}
          />
        ))}

      <Box>
        <Button
          startIcon={<AddCircleOutline />}
          onClick={handleAppendCriteriaRule}
          disabled={isReadOnly || fields.length === availableFacts.length}
        >
          Criteria row
        </Button>
      </Box>
    </FormSection>
  );
};
